import { useEffect, useMemo, useState } from 'react';

import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TreeSelect } from 'primereact/treeselect';

import './MoveProductDetail.scss';
import { IMoveProductDetailProps } from './IMoveProductDetailProps';
import { HttpService, IHttpService } from '../../../services/httpService';
import {
  INotifierService,
  NotifierService,
} from '../../../services/notifierService';

const MoveProductDetail = ({
  productName,
  productId,
  productDetailsListing,
  fetchProductDetails,
  setShouldReload,
}: IMoveProductDetailProps): JSX.Element => {
  const [selectedNodeKey, setSelectedNodeKey] = useState<any>();
  const [treeSelectOptions, setTreeSelectOptions] = useState<any>();
  const [treeSelectDataFlat, setTreeSelectDataFlat] = useState<any>();
  const [firstLoad, setFirstLoad] = useState<boolean>(true);

  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const httpService: IHttpService = useMemo(() => new HttpService(), []);
  const notifierService: INotifierService = useMemo(
    () => new NotifierService(),
    [],
  );

  const convertToTreeSelectData = (productsData: any, keyString = '') => {
    let result = [];

    for (let i = 0; i < productsData.length; i++) {
      let key = `${!!keyString ? keyString + i : i}`;

      let product: any = {
        key: key,
        label: productsData[i].description,
        data: productsData[i].id,
      };

      if (productsData[i].productDetails.length > 0) {
        product.children = convertToTreeSelectData(
          productsData[i].productDetails,
          `${key}-`,
        );
      }

      result.push(product);
    }

    return result;
  };

  const flattenTreeSelectData: any = (elements: any) => {
    let children: any = [];

    const flattenElements = elements.map((el: any) => {
      if (el.children && el.children.length) {
        children = [...children, ...el.children];
      }

      return el;
    });

    return flattenElements.concat(
      children.length ? flattenTreeSelectData(children) : children,
    );
  };

  useEffect(() => {
    if (firstLoad) {
      setTreeSelectOptions(
        convertToTreeSelectData([
          {
            id: null,
            description: 'Top Level',
            active: true,
            divisionCode: null,
            productTypeCode: null,
            parentProductDetailDescription: '',
            parentProductDetailId: undefined,
            productDetails: [],
          },
          ...productDetailsListing,
        ]),
      );
      setFirstLoad(false);
    } else {
      setTreeSelectDataFlat(flattenTreeSelectData(treeSelectOptions));
    }
  }, [firstLoad]);

  const resetDialog = (): void => {
    setIsVisible(false);
    setIsLoading(false);
    document.body.classList.remove('no-scroll');
  };

  const handleMove = async (): Promise<void> => {
    const destinationParentId: number = treeSelectDataFlat.find(
      (p: any) => p.key == selectedNodeKey,
    ).data;

    await httpService
      .put(
        `productDetails/${productId}/changeParent`,
        JSON.stringify({ parentId: destinationParentId }),
        undefined,
        false,
      )
      .catch(err => {
        notifierService.error(err);
      })
      .then(() => {
        fetchProductDetails();
        setSelectedNodeKey(null);
        setIsVisible(false);
        setIsLoading(false);
        document.body.classList.remove('no-scroll');
      });
  };

  const updateDialogFooter: JSX.Element = (
    <>
      <button
        className="btn primary"
        onClick={handleMove}
        disabled={!!!selectedNodeKey}
      >
        Move
      </button>
      <button className="btn secondary" onClick={resetDialog}>
        Cancel
      </button>
    </>
  );

  const pendingFooter: JSX.Element = (
    <div className="move-product-detail__dialog-footer">
      <p>Moving Product Detail...</p>
      <ProgressSpinner />
    </div>
  );

  return (
    <>
      <i
        className="pi pi-arrows-h move-product-detail__action-btn"
        onClick={() => {
          setIsVisible(true);
          document.body.classList.add('no-scroll');
        }}
      ></i>

      <Dialog
        className="move-product-detail__dialog"
        header="Move Product Detail"
        visible={isVisible}
        onHide={resetDialog}
        footer={isLoading ? pendingFooter : updateDialogFooter}
        baseZIndex={9999}
      >
        <div className="move-product-detail__dialog-content">
          <div className='move-product-detail__dialog-row'>
            <p className="move-product-detail__dialog-product-name">Description</p>
            <p className="move-product-detail__dialog-product-value"><strong>{productName}</strong></p>
          </div>
          <label htmlFor="divisionId">Placement</label>
          <TreeSelect
            filter
            options={treeSelectOptions}
            value={selectedNodeKey}
            onChange={e => setSelectedNodeKey(e.value)}
            placeholder="Select Placement"
            panelClassName="move-product-detail__tree-select"
          ></TreeSelect>
        </div>
      </Dialog>
    </>
  );
};

export default MoveProductDetail;
